import { useAuth0 } from "@auth0/auth0-react";
import { FeatureFlags } from "@utils/FeatureFlags/generated-flags";
import { useFlags, useLDClient, useLDClientError } from "launchdarkly-react-client-sdk";
import React from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useAdminData } from "@providers/AdminDataProvider";

import { ONBOARDING_ROLES, roleType } from "../../layouts/config";

interface OnboardingRedirectGateProps {
  children: React.ReactNode;
}

// First-login onboarding gate. Redirect providers who haven't finished
// onboarding into the flow, behind the `provider-onboarding-flow` kill-switch.
// Wraps the authenticated layout (see App.tsx) so it gates every authenticated
// route except /onboarding itself — a deep link can't bypass it. Once in the
// flow, completeProviderOnboarding flips the flag and OnboardingPage refetches
// (and optimistically updates) getLoginProvider before navigating out, so this
// doesn't loop. We require the query to have SUCCEEDED and the flag to be
// strictly `false` (not undefined/loading) so we never flash the redirect while
// data is still settling, and malformed/metadata-less accounts (flag undefined)
// are never gated. Role scoping via ONBOARDING_ROLES excludes caregivers and
// non-portal providers. `?step=`/`?flow=` QA previews live on /onboarding, which
// is excluded here, so they stay reachable.
//
// Kept out of IndexLayout deliberately: that component is already large, and
// folding the gate into it would attribute its (pre-existing) complexity to this
// change. A small dedicated wrapper keeps the gate isolated and testable.
export const OnboardingRedirectGate: React.FC<OnboardingRedirectGateProps> = ({ children }) => {
  const { user, isLoading } = useAuth0();
  const flags = useFlags<FeatureFlags>();
  const ldClient = useLDClient();
  const ldError = useLDClientError();
  const { roles, loginProviderDetailsQuery } = useAdminData();
  const { pathname } = useLocation();

  const appIsBootstrapping = isLoading || (!ldClient && !ldError);
  const onboardingCompleted = loginProviderDetailsQuery.data?.getLoginProvider?.providermetadata?.onboardingCompleted;

  if (
    user &&
    !appIsBootstrapping &&
    flags["provider-onboarding-flow"] &&
    loginProviderDetailsQuery.isSuccess &&
    onboardingCompleted === false &&
    roles.some((role) => ONBOARDING_ROLES.includes(role as roleType)) &&
    pathname !== "/onboarding"
  ) {
    return <Navigate to="/onboarding" />;
  }

  return <>{children}</>;
};
